// footer/SkillButton.tsx
// 「技能」按钮：静态外观还原（设计稿 node 2707:60377）。
// 默认态颜色与「智能体」按钮一致（灰边 #E5E5E5 / 黑字 #0A0A0A / 透明底）；
// 选中态为紫色（紫底 #EEEAFF / 紫边 #DDCDF9 / 紫字 #7C3AED）。
// 点击展开技能下拉面板（SkillPanel），面板宽度跟随编辑器输入框宽度，
// 自适应翻转：优先在输入框下方展开（距框 8px）；下方放不下且上方更宽裕时翻到输入框上方。
// 注意：按钮容器**不能**用 role="button"——消费端常有全局 `[role="button"]{border:0;padding:0;
// background:none}` reset（如 web-agent src/index.less），会清掉本按钮的灰边/内边距/底色，
// 表现为「智能体按钮有样式、技能按钮没样式」（智能体 badge 是纯 div 无 role 故幸免）。
// 改用 data-rte-skill-button 标记，规避招致样式污染的 ARIA role（同 TagNode 已踩过的坑）。
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopupHost } from '../PopupHostContext';
import { SkillPanel, type SkillSelection, type SkillCardActionType, type SkillHeaderActionType } from './SkillPanel';
import type { SkillItem, SkillListQuery, SkillListResult } from '../types';

type Props = {
  disabled?: boolean;
  /** 当前选中技能（null=未选）→ 选中时按钮变紫并显示技能名 */
  selectedSkill?: SkillSelection | null;
  /** 选中/取消技能回调（由 index.tsx 协调与智能体互斥） */
  onSkillSelect?: (skill: SkillSelection | null) => void;
  /** 单独点击技能卡片主体回调（不回填、不改选中态，仅把技能信息抛给外部） */
  onSkillCardClick?: (skill: SkillSelection) => void;
  /** 技能卡片右上角图标点击回调（向输入框外暴露，action 标识图标类型） */
  onSkillAction?: (action: SkillCardActionType, skill: SkillSelection) => void | string | Promise<void | string>;
  /** 技能列表数据源：面板每次打开调一次刷新；删除成功后也刷新 */
  skillListFetch?: (query: SkillListQuery) => Promise<SkillItem[] | SkillListResult>;
  /** 头部「导入技能」/「基于会话创建技能」按钮点击回调，action 区分是哪个按钮 */
  onSkillHeaderAction?: (action: SkillHeaderActionType) => void;
  /** 是否显示「基于会话创建技能」按钮（智能体激活时隐藏）。默认显示 */
  showCreateSkill?: boolean;
  /** 受控：外部控制面板展开/收起。不传时走内部 state（非受控） */
  skillPanelOpen?: boolean;
  /** 面板状态变化时通知外部（点击按钮、选中技能关闭、点击外部关闭） */
  onSkillPanelOpenChange?: (open: boolean) => void;
  /** 「基于当前会话创建技能」按钮禁用状态 */
  createDisabled?: boolean;
  /** 受控模式下面板打开的延迟时间（ms）。用于关闭弹窗后延迟打开面板，避免视觉闪烁 */
  skillPanelOpenDelay?: number;
  /** 技能面板最大高度，支持 px 数值或 CSS 高度值（默认 360px）；小屏时会按可用空间继续收缩 */
  skillPanelHeight?: string | number;
};

const GAP = 4; // 面板与按钮所在编辑器框的间距
const PANEL_BOTTOM_GAP = 8; // 面板与业务容器底部之间的最小间距

// 闪电图标（设计稿 node 2707:60484 的矢量），fill 用 currentColor 跟随文字色，
// 故默认态(黑字)与选中态(紫字)各自自动着色，无需两份 SVG。
// 导出供 ToolbarOverflow 的「更多」菜单里技能代理行复用（避免重复维护 SVG）。
export const SkillIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M10.4103 0.600098C11.2711 0.600098 11.9863 1.2657 11.9863 2.1081C11.9863 2.2985 11.9479 2.4873 11.8751 2.6641L10.9119 4.9921H12.6255C13.4455 4.9921 14.1327 5.5961 14.1959 6.3817L14.2007 6.5009C14.2007 6.8905 14.0431 7.2641 13.7639 7.5433L6.28226 15.0321C6.05292 15.2576 5.74735 15.3888 5.42594 15.3999C5.10452 15.411 4.79062 15.3012 4.54626 15.0921C4.19986 14.7929 4.05346 14.3297 4.16706 13.9113L5.20146 9.4593H3.28146C2.63906 9.4593 2.09506 8.9873 2.03666 8.3649L2.03186 8.2537C2.03186 8.0993 2.06306 7.9457 2.12066 7.8073L4.67986 1.5513C4.92146 0.970498 5.50306 0.600098 6.14306 0.600098H10.4103ZM10.4103 1.8001H6.14386C5.97906 1.8001 5.83986 1.8889 5.78866 2.0105L3.23586 8.2521C3.24066 8.2521 3.24626 8.2537 3.25346 8.2553L3.28306 8.2585H5.55506C5.62866 8.2585 5.70146 8.2665 5.77186 8.2833C6.26786 8.4001 6.58466 8.8937 6.45506 9.3713L5.33506 14.1857L5.35586 14.1969C5.37426 14.2017 5.39586 14.2009 5.41266 14.1953L5.43426 14.1833L12.9151 6.6953C12.9414 6.67028 12.9625 6.64029 12.9772 6.60706C12.9919 6.57383 12.9999 6.53802 13.0007 6.5017C13.0007 6.3417 12.8415 6.1937 12.6247 6.1937H10.4887C10.3799 6.1937 10.2719 6.1745 10.1687 6.1377C10.0535 6.09806 9.94761 6.03536 9.85749 5.95342C9.76737 5.87149 9.6949 5.77204 9.64451 5.66115C9.59412 5.55026 9.56687 5.43027 9.56442 5.30849C9.56197 5.18672 9.58437 5.06572 9.63026 4.9529L10.7671 2.2049C10.7797 2.17445 10.7862 2.14184 10.7863 2.1089C10.7863 1.9481 10.6279 1.8001 10.4111 1.8001H10.4103ZM7.10386 2.6433C7.25151 2.70252 7.3696 2.81796 7.43215 2.96423C7.49471 3.1105 7.49662 3.27562 7.43746 3.4233L6.79746 5.0233C6.73604 5.16775 6.62045 5.28238 6.4755 5.34261C6.33054 5.40283 6.16775 5.40385 6.02205 5.34544C5.87635 5.28704 5.75934 5.17386 5.69612 5.03018C5.6329 4.88651 5.6285 4.72378 5.68386 4.5769L6.32386 2.9769C6.38308 2.82925 6.49852 2.71116 6.64479 2.6486C6.79106 2.58605 6.95618 2.58414 7.10386 2.6433Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.2"
    />
  </svg>
);

type PanelPos = { left: number; top?: number; bottom?: number; width: number; maxHeight: number };

export const SkillButton: React.FC<Props> = React.memo(({ disabled, selectedSkill, onSkillSelect, onSkillCardClick, onSkillAction, skillListFetch, onSkillHeaderAction, showCreateSkill, skillPanelOpen, onSkillPanelOpenChange, createDisabled, skillPanelOpenDelay, skillPanelHeight }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  // 受控/非受控分离：外部传了 skillPanelOpen 则以它为准，否则走内部 state
  const isControlled = skillPanelOpen !== undefined;
  // 受控模式 + 配置了 delay：skillPanelOpen 从 false→true 时延迟生效
  const [delayedOpen, setDelayedOpen] = useState(skillPanelOpen ?? false);
  const delayTimerRef = useRef(0);
  useEffect(() => {
    if (!isControlled) return;
    if (skillPanelOpen) {
      if (skillPanelOpenDelay && skillPanelOpenDelay > 0) {
        delayTimerRef.current = window.setTimeout(() => setDelayedOpen(true), skillPanelOpenDelay);
      } else {
        setDelayedOpen(true);
      }
    } else {
      window.clearTimeout(delayTimerRef.current);
      setDelayedOpen(false);
    }
    return () => window.clearTimeout(delayTimerRef.current);
  }, [isControlled, skillPanelOpen, skillPanelOpenDelay]);
  const open = isControlled ? delayedOpen : internalOpen;
  const setOpen = useCallback((next: boolean | ((v: boolean) => boolean)) => {
    const value = typeof next === 'function' ? next(open) : next;
    if (!isControlled) setInternalOpen(value);
    onSkillPanelOpenChange?.(value);
  }, [isControlled, open, onSkillPanelOpenChange]);
  const [pos, setPos] = useState<PanelPos | null>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const host = usePopupHost();

  // 找到按钮所在的编辑器根容器（.rich-text-editor），面板宽度/水平定位以它为准
  const getAnchorRect = useCallback((): DOMRect | null => {
    const editor = badgeRef.current?.closest('.rich-text-editor');
    return (editor ?? badgeRef.current)?.getBoundingClientRect() ?? null;
  }, []);

  // 计算面板位置：宽度=编辑框宽度。自适应翻转——优先在编辑框**下方**展开（距框 GAP）；
  // 当下方可用空间放不下面板、且上方空间更大时，翻到编辑框**上方**展开。
  // 关键：朝上时用 bottom 锚定（面板底边贴编辑框顶边 − GAP），**不依赖测得的面板高度**——
  // 这样技能列表异步 fetch 前后面板高度变化也不会让位置漂移（之前用 top=anchor.top−panelH，
  // 首帧 loading 态 panelH 偏小 → 定位偏上，数据到位变高才需手动滚动触发重算才归位）。
  const recompute = useCallback(() => {
    const anchor = getAnchorRect();
    if (!anchor) return;
    // 浮层可能挂在业务侧带 overflow:hidden 的局部容器中。此时真正的可视边界是
    // 「浏览器视口 ∩ Portal 宿主」，不能只按 window.innerHeight 计算，否则面板会先被
    // 业务容器裁切，看起来像底部预留的 GAP 没有生效。
    const hostRect = host?.getBoundingClientRect();
    const hasMeasurableHost = !!hostRect && hostRect.height > 0;
    const visibleTop = hasMeasurableHost ? Math.max(0, hostRect.top) : 0;
    const visibleBottom = hasMeasurableHost
      ? Math.min(window.innerHeight, hostRect.bottom)
      : window.innerHeight;
    // 下方空间分别预留输入框间距与业务容器底部间距；上方沿用原来的双 GAP 规则。
    const spaceBelow = Math.max(0, visibleBottom - anchor.bottom - GAP - PANEL_BOTTOM_GAP);
    const spaceAbove = Math.max(0, anchor.top - visibleTop - GAP * 2);
    // 面板自然高度（未挂载时退化为 0，首帧后 layoutEffect / RO 重算会拿到真实值）。
    // 仅用于「判断要不要翻上」，不参与朝上时的 top 计算。
    const desired = panelRef.current?.scrollHeight ?? 0;
    const placeAbove = desired > spaceBelow && spaceAbove > spaceBelow;
    const maxHeight = placeAbove ? spaceAbove : spaceBelow;
    // anchor.* 是视口坐标，但 position:fixed 的包含块是 portal 容器（浮层宿主）的
    // 最近 transform/filter 祖先，而非视口。减去宿主自身的视口偏移，把视口坐标
    // 换算成相对包含块的坐标。宿主正常铺满视口(0,0)时偏移为 0，不影响其他场景。
    const originEl = panelRef.current?.offsetParent ?? host;
    const origin = originEl?.getBoundingClientRect();
    const ox = origin?.left ?? 0;
    const oy = origin?.top ?? 0;
    const left = anchor.left - ox;
    if (placeAbove) {
      // 朝上：用 bottom 锚定面板底边到编辑框顶边 − GAP。bottom 相对包含块底边，
      // 故 = 包含块高度 −（编辑框顶边视口坐标 − GAP − 包含块顶边视口坐标）。
      const originH = origin?.height ?? window.innerHeight;
      const bottom = originH - (anchor.top - GAP - oy);
      setPos({ left, bottom, width: anchor.width, maxHeight });
    } else {
      // 朝下：顶边 = 编辑框底边 + 间距
      setPos({ left, top: anchor.bottom + GAP - oy, width: anchor.width, maxHeight });
    }
  }, [getAnchorRect, host]);

  // 展开后测量面板真实高度再定位（首帧 panelRef 已挂载，scrollHeight 才有值，据此判翻转），
  // 并监听滚动/缩放 + 用 ResizeObserver 观察面板自身：技能列表异步 fetch 到位后面板变高，
  // 无 scroll/resize 事件也能触发重算（修「点开偏上、手动滚动才归位」）。
  // 同时观察锚点（.rich-text-editor）：输入框内容变多、编辑器长高时 anchor.bottom 改变，
  // 但既不触发 scroll 也不触发 resize；不观察锚点的话朝下展开的面板会停在旧位置、与编辑器底边脱节。
  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    const raf = requestAnimationFrame(recompute);
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    const panelRo = panelRef.current ? new ResizeObserver(recompute) : null;
    if (panelRo && panelRef.current) panelRo.observe(panelRef.current);
    const anchorEl = badgeRef.current?.closest('.rich-text-editor') as HTMLElement | null;
    const anchorRo = anchorEl ? new ResizeObserver(recompute) : null;
    if (anchorRo && anchorEl) anchorRo.observe(anchorEl);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
      panelRo?.disconnect();
      anchorRo?.disconnect();
    };
  }, [open, recompute]);

  // 点击外部关闭（仅非受控模式生效）。
  // 受控模式下面板开关完全由外部 skillPanelOpen 决定，点击外部不应自动收起——
  // 否则关闭弹窗瞬间的 mousedown 触发 onSkillPanelOpenChange(false)，外部再重新
  // 设回 true 时会产生"闪一下"的体验问题。
  useEffect(() => {
    if (!open || isControlled) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (badgeRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      if (t instanceof Element && t.closest('.rich-text-editor')) return;
      // 删除确认 Popconfirm / 分享码 Popover 浮层挂在 .rte-popup-host（非 panelRef 后代），
      // 点其内部不应判为「点面板外」而关掉整个面板。命中任一浮层则忽略。
      if (t instanceof Element && t.closest('.rte-skill-delete-confirm, .rte-skill-share-popover')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, isControlled, setOpen]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (disabled) return;
    setOpen((v) => !v);
  }, [disabled, setOpen]);

  // 选中/取消技能：选中后关闭面板；取消（传 null）保持面板开着便于改选
  const handleSkillSelect = useCallback(
    (skill: SkillSelection | null) => {
      onSkillSelect?.(skill);
      if (skill) setOpen(false);
    },
    [onSkillSelect, setOpen],
  );

  const selected = !!selectedSkill;
  // 选中态优先：已选技能 → 紫底紫边；仅展开未选 → 紫底无边框（active）
  const badgeClass = `rte-skill-badge${selected ? ' rte-skill-badge-selected' : open ? ' rte-skill-badge-active' : ''}`;

  return (
    <>
      <div
        ref={badgeRef}
        className={badgeClass}
        onClick={handleClick}
        data-rte-skill-button="true"
        aria-pressed={open || selected}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <SkillIcon />
        {/* 固定显示分类名「技能」——技能名已在输入框内的技能 tag 展示，此处不重复。 */}
        <span className="rte-skill-text">技能</span>
        {/* 激活态（面板打开中，或已选中具体技能）右侧显示 × 关闭。stopPropagation 防止冒泡触发
            handleClick 又把面板打开。点击：关闭面板 + 退出技能（onSkillSelect(null) 与输入框技能 tag
            同一退出路径，共享 selectedSkill 状态自动双向同步）→ 收回「更多」、回到之前。 */}
        {(open || selected) && (
          <i
            className="iconfont icon-guanbi rte-tool-close"
            role="button"
            aria-label="退出技能"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (disabled) return;
              setOpen(false);
              onSkillSelect?.(null);
            }}
          />
        )}
      </div>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="rte-skill-panel-popup"
            data-placement={pos?.bottom !== undefined ? 'top' : 'bottom'}
            style={{
              position: 'fixed',
              left: pos?.left ?? 0,
              // top / bottom 二选一：朝下设 top、朝上设 bottom（面板底边锚定，撑高方向朝上不漂移）
              top: pos?.top,
              bottom: pos?.bottom,
              width: pos?.width,
              // 抬高层级，压在编辑器内其他浮层/内容之上（与 antd select/datepicker 等下拉同级 9999）
              zIndex: 9999,
              // 面板高度受限于编辑框下方可用空间；用 flex column 让内部面板成为受 maxHeight 约束的
              // flex item（百分比 max-height 在父无定高时失效，故改用 flex 约束），内容超出时面板内部滚动
              maxHeight: pos?.maxHeight,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              // 圆角与内层 .rte-skill-panel(border-radius:16) 对齐：外层 overflow:hidden 但无圆角时，
              // 内层圆角拐角之外、外层直角之内会露出一块白角（用户：背景溢出一块）。外层同设 16px
              // 圆角后裁切边界与内层一致，白角消失。
              borderRadius: 16,
              // 定位计算完成前先隐藏，避免左上角闪现
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            <SkillPanel selectedId={selectedSkill?.id ?? null} onSelect={handleSkillSelect} onCardClick={onSkillCardClick} onAction={onSkillAction} skillListFetch={skillListFetch} onHeaderAction={onSkillHeaderAction} showCreateSkill={showCreateSkill} createDisabled={createDisabled} panelMaxHeight={skillPanelHeight} />
          </div>,
          host ?? document.body,
        )}
    </>
  );
});
SkillButton.displayName = 'SkillButton';
